// Aura Farming System - Calculations & Rankings

/**
 * Aura Tier System
 * Fire Aura = High ranks (good drip)
 * L Aura = Low ranks (mid/cringe)
 */
export const AURA_TIERS = [
  {
    min: 90,
    max: 100,
    tier: 'S+',
    fireRank: 'Aura God',
    lRank: null,
    badge: '/auragodbadge.png',
    color: '#FFD700',
    glow: 'infinite-glow',
    emoji: '👑',
    description: 'Gold crown + glowing halo',
  },
  {
    min: 80,
    max: 89,
    tier: 'S',
    fireRank: 'Fire Slayer',
    lRank: null,
    badge: '/fireslayerbadge.png',
    color: '#FF6B00',
    glow: 'maxxed-heat',
    emoji: '🔥',
    description: 'Flame border + sword',
  },
  {
    min: 70,
    max: 79,
    tier: 'A',
    fireRank: 'Clean Flex',
    lRank: null,
    badge: '/cleanflexbadge.png',
    color: '#9370DB',
    glow: 'bussin-beam',
    emoji: '💪',
    description: 'Muscle + sparkles',
  },
  {
    min: 60,
    max: 69,
    tier: 'B',
    fireRank: 'Mid Noob',
    lRank: 'Mid Noob',
    badge: '/midnoobbadge.png',
    color: '#808080',
    glow: 'vibe-check',
    emoji: '😐',
    description: 'Mid energy',
  },
  {
    min: 50,
    max: 59,
    tier: 'C',
    fireRank: 'Basic Vibe',
    lRank: 'Basic Vibe',
    badge: '/basicvibebadge.png',
    color: '#808080',
    glow: 'vibe-check',
    emoji: '↔️',
    description: 'Neutral territory',
  },
  {
    min: 40,
    max: 49,
    tier: 'D',
    fireRank: null,
    lRank: 'L Drain',
    badge: '/ldrainbadge.png',
    color: '#FF0000',
    glow: 'grind-mode',
    emoji: '🧛',
    description: 'Vampire fangs + red drain effect',
  },
  {
    min: 30,
    max: 39,
    tier: 'E',
    fireRank: null,
    lRank: 'Total Cringe',
    badge: '/totalcringebadge.png',
    color: '#8B4513',
    glow: 'respawn-vibes',
    emoji: '🤡',
    description: 'Clown face + trash emoji',
  },
  {
    min: 20,
    max: 29,
    tier: 'F',
    fireRank: null,
    lRank: 'Cooked',
    badge: '/cookedbadge.png',
    color: '#4B0000',
    glow: 'cooked-mode',
    emoji: '💀',
    description: 'Completely cooked',
  },
  {
    min: 0,
    max: 19,
    tier: 'F-',
    fireRank: null,
    lRank: 'Skibidi',
    badge: '/skibidibadge.png',
    color: '#2B0000',
    glow: 'toilet-mode',
    emoji: '🚽',
    description: 'Rock bottom',
  },
];

/**
 * Calculate Aura percentage from swipes
 * Formula: (Fire Swipes / Total Swipes) × 100
 */
export function calculateAuraPercent(fireSwipes, leftSwipes) {
  const totalSwipes = fireSwipes + leftSwipes;
  if (totalSwipes === 0) return 0;
  return Math.round((fireSwipes / totalSwipes) * 100);
}

/**
 * Calculate Net Aura score
 * Fire swipes = +2 points
 * Left swipes = -1 point
 */
export function calculateNetAura(fireSwipes, leftSwipes) {
  return (fireSwipes * 2) - leftSwipes;
}

/**
 * Get Aura tier based on percentage
 * @param {number} auraPercent - Aura percentage (0-100)
 * @param {boolean} isHighAura - true for Fire Aura ranks, false for L Aura ranks
 */
export function getAuraTier(auraPercent, isHighAura = true) {
  for (const tier of AURA_TIERS) {
    if (auraPercent >= tier.min && auraPercent <= tier.max) {
      const rank = isHighAura ? tier.fireRank : tier.lRank;
      return {
        ...tier,
        rank: rank || (isHighAura ? tier.lRank : tier.fireRank), // Fallback to opposite if null
      };
    }
  }
  // Default to lowest tier
  const defaultTier = AURA_TIERS[AURA_TIERS.length - 1];
  return {
    ...defaultTier,
    rank: defaultTier.lRank || defaultTier.fireRank,
  };
}

/**
 * Get badge based on Aura percentage with new tier system
 * @param {number} auraPercent - Aura percentage (0-100)
 * @param {number} totalSwipes - Total number of swipes (optional)
 */
export function getAuraBadge(auraPercent, totalSwipes = null) {
  // Check if avatar has never been swiped
  if (totalSwipes === 0) {
    return {
      type: 'neutral',
      label: 'Zero Aura',
      emoji: '/zeroaurabadge.png',
      color: '#808080',
      bgColor: 'rgba(128, 128, 128, 0.2)',
      tier: 'Unrated',
    };
  }

  // Find the tier for this percentage
  for (const tier of AURA_TIERS) {
    if (auraPercent >= tier.min && auraPercent <= tier.max) {
      // Determine if it's good or bad aura
      const isGood = auraPercent >= 50;
      const rank = isGood ? tier.fireRank : tier.lRank;

      return {
        type: isGood ? 'good' : 'bad',
        label: rank || (isGood ? tier.lRank : tier.fireRank), // Fallback to opposite if null
        emoji: tier.badge,
        color: tier.color,
        bgColor: isGood ? 'rgba(255, 215, 0, 0.2)' : 'rgba(255, 0, 0, 0.2)',
        tier: tier.tier,
      };
    }
  }

  // Default fallback
  return {
    type: 'bad',
    label: 'Skibidi',
    emoji: '/skibidibadge.png',
    color: '#2B0000',
    bgColor: 'rgba(255, 0, 0, 0.2)',
    tier: 'F-',
  };
}

/**
 * Calculate full Aura stats for an avatar
 */
export function calculateAuraStats(fireSwipes = 0, leftSwipes = 0) {
  const auraPercent = calculateAuraPercent(fireSwipes, leftSwipes);
  const netAura = calculateNetAura(fireSwipes, leftSwipes);
  const totalSwipes = fireSwipes + leftSwipes;
  const isHighAura = auraPercent >= 50;
  const tier = getAuraTier(auraPercent, isHighAura);

  return {
    auraPercent,
    netAura,
    totalSwipes,
    fireSwipes,
    leftSwipes,
    tier,
    isHighAura,
  };
}

/**
 * Shard Earning Rules
 */
export const SHARD_EARNINGS = {
  FREE_SWIPE: 1,        // Free swipe (5/day)
  PAID_SWIPE: 2,        // Paid credit swipe
  DAILY_QUEST_20: 5,    // Complete 20 swipes daily quest
  L_DRAIN_10: 3,        // Drain 10 L Aura
  REFER_FRIEND: 10,     // Refer a friend who subs
};

/**
 * Daily free swipe limit
 */
export const FREE_SWIPES_PER_DAY = 5;

/**
 * Boost Shop Items
 */
export const BOOSTS = [
  {
    id: 'visibility_bump',
    name: 'Visibility Bump',
    description: 'Top feed for 1 hour (+50% swipes)',
    cost: 10,
    emoji: '🌟',
    effect: 'visibility_1h',
    hype: 'Glow Surge! 🌟',
  },
  {
    id: 'vote_multiplier',
    name: 'Vote Multiplier',
    description: 'Next 10 swipes count x2 for Aura',
    cost: 20,
    emoji: '🔥',
    effect: 'multiplier_10',
    hype: 'Fire Amp! 🔥',
  },
  {
    id: 'l_shield',
    name: 'L Shield',
    description: 'Ignore next 5 left swipes',
    cost: 15,
    emoji: '🛡️',
    effect: 'shield_5',
    hype: 'Drain Proof! 🛡️',
  },
  {
    id: 'resubmit_glow',
    name: 'Resubmit Glow',
    description: 'New entry with starter Aura',
    cost: 50,
    emoji: '✨',
    effect: 'resubmit',
    hype: 'Re-Farm God Mode ✨',
  },
];

/**
 * Get boost by ID
 */
export function getBoost(boostId) {
  return BOOSTS.find(b => b.id === boostId);
}

/**
 * Check if user can afford boost
 */
export function canAffordBoost(shards, boostId) {
  const boost = getBoost(boostId);
  return boost && shards >= boost.cost;
}

/**
 * Daily Quest Definitions
 */
export const DAILY_QUESTS = [
  {
    id: 'swipe_20',
    name: 'Swipe Master',
    description: 'Swipe 20 times',
    target: 20,
    reward: 5,
    emoji: '⚡',
  },
  {
    id: 'l_drain_10',
    name: 'L Harvester',
    description: 'Give 10 L Aura votes',
    target: 10,
    reward: 3,
    emoji: '💀',
  },
  {
    id: 'fire_streak_5',
    name: 'Fire Streak',
    description: 'Give 5 Fire Aura votes in a row',
    target: 5,
    reward: 4,
    emoji: '🔥',
  },
];

/**
 * Get hype message based on swipe result
 */
export function getSwipeHypeMessage(isFire, auraStats) {
  if (isFire) {
    const messages = [
      'W Vibe Detected! 🔥',
      'Fire Aura! 🔥',
      'Bussin\'! 🔥',
      'Drip God Energy! ✨',
      'Absolute Slay! 👑',
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  } else {
    const messages = [
      'Mid Energy – Fume! 💀',
      'L Aura Drain! 😐',
      'Not It... 👎',
      'Zero Energy! 😬',
      'Respawn Needed! 💫',
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  }
}

/**
 * Format Aura display text
 */
export function formatAuraDisplay(auraStats) {
  const { auraPercent, fireSwipes, totalSwipes, tier } = auraStats;

  return {
    percent: `${auraPercent}%`,
    swipes: `${fireSwipes} / ${totalSwipes}`,
    rank: tier.rank,
    badge: tier.badge,
    emoji: tier.emoji,
  };
}
